// Tiny SVG donut chart showing server RAM usage. Lives in the topbar
// right slot, immediately left of the ⋯ overflow button. Color shifts
// green → yellow → red as % used climbs (mirrors any IT monitoring tool).
// Pure read-only chip — no click handler; tooltip on hover shows actuals.
//
// Data source: `metrics` polled every 8s by the store from /api/metrics.
// Renders null if metrics haven't loaded yet (avoid layout shift flash).

import { useSession } from "../store/session";

function colorForPct(pct: number): string {
  if (pct < 60) return "#5ed688";   // soft green
  if (pct < 80) return "#f6c453";   // amber
  return "#ef4444";                 // red
}

export default function RamChip() {
  const metrics = useSession((s) => s.metrics);
  if (!metrics) return null;

  const { ramUsedMB, ramTotalMB, ramUsedPct, load1, sessionsActive } = metrics;
  const color = colorForPct(ramUsedPct);

  // SVG donut math: circumference = 2πr. Stroke-dashoffset reveals %.
  const r = 9;
  const c = 2 * Math.PI * r;
  const filled = (Math.min(100, Math.max(0, ramUsedPct)) / 100) * c;
  const offset = c - filled;

  const ramGb = (ramUsedMB / 1024).toFixed(1);
  const totalGb = (ramTotalMB / 1024).toFixed(0);
  const tooltip = `RAM ${ramGb} / ${totalGb} GB (${ramUsedPct}%) · load ${load1.toFixed(2)} · ${sessionsActive} session${sessionsActive === 1 ? "" : "s"}`;

  return (
    <div className="ramChip" title={tooltip} aria-label={tooltip}>
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
        {/* Track */}
        <circle
          cx="12"
          cy="12"
          r={r}
          fill="none"
          stroke="rgba(255,255,255,0.14)"
          strokeWidth="3.2"
        />
        {/* Fill */}
        <circle
          cx="12"
          cy="12"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="3.2"
          strokeDasharray={`${c}`}
          strokeDashoffset={`${offset}`}
          strokeLinecap="round"
          transform="rotate(-90 12 12)"
          style={{ transition: "stroke-dashoffset 0.4s ease, stroke 0.4s ease" }}
        />
      </svg>
    </div>
  );
}
